import { removeUndoMarker, showUndoMarker } from "./undoMarker";

const midpointVertices = { n: ["nw", "ne"], e: ["ne", "se"], s: ["sw", "se"], w: ["nw", "sw"] };

const createSupplementaryPoint = (parentId, coord, handleType, handleId) => ({
    type: "Feature",
    properties: {
        meta: handleType === "midpoint" ? "midpoint" : "vertex",
        parent: parentId,
        handleType,
        handleId,
        active: "true",
    },
    geometry: {
        type: "Point",
        coordinates: coord,
    },
});

const rectangleFromPolygon = (coords) => {
    const ring = coords.slice(0, -1);
    const lngs = ring.map(([lng]) => lng);
    const lats = ring.map(([, lat]) => lat);

    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    return {
        nw: [minLng, maxLat],
        ne: [maxLng, maxLat],
        se: [maxLng, minLat],
        sw: [minLng, minLat],
    };
};

const rectangleToCoordinates = (rect) => [rect.nw, rect.ne, rect.se, rect.sw, rect.nw];

const resizeFromVertex = (rect, vertex, dragCoord) => {
    const [lng, lat] = dragCoord;

    switch (vertex) {
        case "nw": {
            const se = rect.se;
            return {
                nw: [lng, lat],
                ne: [se[0], lat],
                se,
                sw: [lng, se[1]],
            };
        }
        case "ne": {
            const sw = rect.sw;
            return {
                nw: [sw[0], lat],
                ne: [lng, lat],
                se: [lng, sw[1]],
                sw,
            };
        }
        case "se": {
            const nw = rect.nw;
            return {
                nw,
                ne: [lng, nw[1]],
                se: [lng, lat],
                sw: [nw[0], lat],
            };
        }
        case "sw": {
            const ne = rect.ne;
            return {
                nw: [lng, ne[1]],
                ne,
                se: [ne[0], lat],
                sw: [lng, lat],
            };
        }
        default:
            return rect;
    }
};

const clampRectangle = (rect, minDelta = 0.00001) => {
    let west = rect.nw[0];
    let east = rect.ne[0];
    let north = rect.nw[1];
    let south = rect.sw[1];

    if (east - west < minDelta) {
        if (east >= west) {
            east = west + minDelta;
        } else {
            west = east - minDelta;
        }
    }

    if (north - south < minDelta) {
        if (north >= south) {
            south = north - minDelta;
        } else {
            north = south + minDelta;
        }
    }

    return {
        nw: [west, north],
        ne: [east, north],
        se: [east, south],
        sw: [west, south],
    };
};

const getHandleCursor = (handleType, handleId) => {
    if (handleType === "vertex") {
        switch (handleId) {
            case "nw":
            case "se":
                return "nwse-resize";
            case "ne":
            case "sw":
                return "nesw-resize";
            default:
                return "";
        }
    }

    if (handleType === "midpoint") {
        switch (handleId) {
            case "n":
            case "s":
                return "ns-resize";
            case "e":
            case "w":
                return "ew-resize";
            default:
                return "";
        }
    }

    return "";
};

const getMidpoint = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];

const resizeFromMidPoint = (rect, midpoint, dragCoord) => {
    const [lng, lat] = dragCoord;

    switch (midpoint) {
        case "n":
            return {
                nw: [rect.nw[0], lat],
                ne: [rect.ne[0], lat],
                se: rect.se,
                sw: rect.sw,
            };
        case "s":
            return {
                nw: rect.nw,
                ne: rect.ne,
                se: [rect.se[0], lat],
                sw: [rect.sw[0], lat],
            };
        case "w":
            return {
                nw: [lng, rect.nw[1]],
                ne: rect.ne,
                se: rect.se,
                sw: [lng, rect.sw[1]],
            };
        case "e":
            return {
                nw: rect.nw,
                ne: [lng, rect.ne[1]],
                se: [lng, rect.se[1]],
                sw: rect.sw,
            };
        default:
            return rect;
    }
};

const DirectSelectRectangle = {
    onSetup(options) {
        const featureId = options.featureId;
        const feature = this.getFeature(featureId);
        const rect = rectangleFromPolygon(feature.getCoordinates()[0]);

        this.setSelected(featureId);
        this.updateUIClasses({ mouse: "move" });
        this.setActionableState({
            combineFeatures: false,
            uncombineFeatures: false,
            trash: true,
        });

        const state = {
            featureId,
            feature,
            rectangle: rect,
            dragHandle: null,
            isDragging: false,
        };

        state.mapClickHandler = (e) => {
            if (state.undoMarker && !state.undoMarker.getElement().contains(e.target)) {
                removeUndoMarker(state);
            }
        };
        this.map.getContainer().addEventListener("mousedown", state.mapClickHandler, true);

        return state;
    },

    toDisplayFeatures(state, geojson, display) {
        if (geojson.properties.id === state.featureId) {
            geojson.properties.active = "true";
            display(geojson);

            display(createSupplementaryPoint(state.featureId, state.rectangle.nw, "vertex", "nw"));
            display(createSupplementaryPoint(state.featureId, state.rectangle.ne, "vertex", "ne"));
            display(createSupplementaryPoint(state.featureId, state.rectangle.se, "vertex", "se"));
            display(createSupplementaryPoint(state.featureId, state.rectangle.sw, "vertex", "sw"));

            display(
                createSupplementaryPoint(
                    state.featureId,
                    getMidpoint(state.rectangle.nw, state.rectangle.ne),
                    "midpoint",
                    "n"
                )
            );
            display(
                createSupplementaryPoint(
                    state.featureId,
                    getMidpoint(state.rectangle.ne, state.rectangle.se),
                    "midpoint",
                    "e"
                )
            );
            display(
                createSupplementaryPoint(
                    state.featureId,
                    getMidpoint(state.rectangle.sw, state.rectangle.se),
                    "midpoint",
                    "s"
                )
            );
            display(
                createSupplementaryPoint(
                    state.featureId,
                    getMidpoint(state.rectangle.nw, state.rectangle.sw),
                    "midpoint",
                    "w"
                )
            );

            return;
        }

        display(geojson);
    },

    onMouseMove(state, e) {
        if (!this.map) return;

        if (state.isDragging && state.dragHandle) {
            this.map.getCanvas().style.cursor = getHandleCursor(state.dragHandle.handleType, state.dragHandle.handleId);
            return;
        }

        const props = e.featureTarget?.properties;

        if (
            props &&
            props.parent === state.featureId &&
            props.meta === "vertex" &&
            (props.handleType === "vertex" || props.handleType === "midpoint")
        ) {
            this.map.getCanvas().style.cursor = getHandleCursor(props.handleType, props.handleId);
        } else {
            this.map.getCanvas().style.cursor = "";
        }
    },

    onMouseDown(state, e) {
        const props = e.featureTarget?.properties;

        if (!props) return;
        if (props.parent !== state.featureId) return;
        if (props.handleType !== "vertex" && props.handleType !== "midpoint") return;

        removeUndoMarker(state);
        state.previousCoordinates = JSON.parse(JSON.stringify(state.feature.getCoordinates()));
        state.previousRectangle = JSON.parse(JSON.stringify(state.rectangle));

        state.dragHandle = {
            handleType: props.handleType,
            handleId: props.handleId,
        };
        state.isDragging = true;

        if (this.map?.dragPan) {
            this.map.dragPan.disable();
        }
        if (this.map) {
            this.map.getCanvas().style.cursor = getHandleCursor(props.handleType, props.handleId);
        }
    },

    onDrag(state, e) {
        if (!state.dragHandle) return;

        const dragCoord = [e.lngLat.lng, e.lngLat.lat];
        let rect;
        if (state.dragHandle.handleType === "vertex") {
            rect = resizeFromVertex(state.rectangle, state.dragHandle.handleId, dragCoord);
        } else if (state.dragHandle.handleType === "midpoint") {
            rect = resizeFromMidPoint(state.rectangle, state.dragHandle.handleId, dragCoord);
        } else {
            return;
        }

        rect = clampRectangle(rect);
        state.rectangle = rect;
        state.feature.setCoordinates([rectangleToCoordinates(rect)]);
    },

    onMouseUp(state) {
        if (!state.dragHandle) return;

        const handleId = state.dragHandle.handleId;
        const handleType = state.dragHandle.handleType;
        state.dragHandle = null;
        state.isDragging = false;

        if (this.map?.dragPan) {
            this.map.dragPan.enable();
        }

        if (this.map) {
            this.map.getCanvas().style.cursor = "";
        }

        this.map.fire("draw.update", {
            features: [state.feature.toGeoJSON()],
            action: "change_coordinates",
        });

        const coord =
            handleType === "midpoint"
                ? getMidpoint(
                      state.rectangle[midpointVertices[handleId][0]],
                      state.rectangle[midpointVertices[handleId][1]]
                  )
                : state.rectangle[handleId];
        showUndoMarker(this.map, state, coord);
    },

    onStop(state) {
        this.map.getContainer().removeEventListener("mousedown", state.mapClickHandler, true);
        removeUndoMarker(state);
        this.updateUIClasses({ mouse: "none" });
        if (this.map?.dragPan) {
            this.map.dragPan.enable();
        }
    },

    onTrash(state) {
        this.deleteFeature([state.featureId], { silent: false });
        this.changeMode("simple_select");
    },
};

export default DirectSelectRectangle;
